import { fabric } from 'fabric';
import CollaborationBehaviors from './CollaborationBehaviors';
import ShapeBase from '../shapes/ShapeBase';
import { recordAnalyticsForShapeCreation } from './AnalyticsBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import LockBehaviors from './LockBehaviors';
import ImageCache from '../utils/images/imageCache';
import type WhiteboardController from '../controllers/WhiteboardController';
import { svgToIcon } from '../utils/svg';

const defaultControls = fabric.Object.prototype.controls;

const STAMP_DEFAULT_SIZE = 35;

// An emoji "stamped" onto the canvas.
interface SvgStamp
  extends fabric.Object,
    LockBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    FrameMemberBehaviors {}

@LockBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
class SvgStamp extends fabric.Object {
  type = 'svgStamp';
  noScaleCache = false;
  // So edges are sharp on large objects at high scales
  objectCaching = false;

  svg: string;
  imageCache: ImageCache;

  uniformScaling = true;
  snapAngle = 45;
  snapThreshold = 5;

  // Simple controls for stamps.
  controls = {
    tl: defaultControls.tl,
    tr: defaultControls.tr,
    bl: defaultControls.bl,
    br: defaultControls.br,
    mtr: ShapeBase.rotateControl(),
  };

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'left',
    'top',
    'scaleX',
    'scaleY',
    'angle',
    'container',
    'svg',
  ];

  constructor(options: {
    controller: WhiteboardController;
    [key: string]: any;
  }) {
    super({
      ...options,
      width: STAMP_DEFAULT_SIZE,
      height: STAMP_DEFAULT_SIZE,
      angle: options.angle || SvgStamp._randomRotation(),
    });
    this.initializeFrameMember();
    this.initializeLockBehaviors(options.locked);

    if (this.svg) {
      this.imageCache = new ImageCache(
        this.svg,
        this.width,
        this.height,
        this.imageReady.bind(this)
      );
    }

    this.on('created', () =>
      recordAnalyticsForShapeCreation(this.type, {
        'Stamp-type': this.svg,
      })
    );
  }

  private imageReady() {
    // This callback only happens for the first object using the cache, but
    // other objects using the same image also need to be re-rendered, so we
    // must completely refresh the render.
    this.controller.renderManager.refreshAll();
    this.canvas?.requestRenderAll();
  }

  private static _randomRotation() {
    const offset = 20;
    return Math.random() * (2 * offset) - offset;
  }

  hasExternalContent(): boolean {
    return this.canvas?.isHTMLExport();
  }

  renderExternalContent(): React.JSX.Element {
    return svgToIcon(
      this.imageCache.getSvgString({ width: '100%', height: '100%' })!
    );
  }

  _render(ctx: CanvasRenderingContext2D) {
    if (this.hasExternalContent()) return;

    if (!this.imageCache.image?.complete) {
      this.imageCache.renderPlaceholder(ctx, this);
      return;
    }

    const { width, height } = this;

    const x = -width / 2;
    const y = -height / 2;
    ctx.drawImage(this.imageCache.image, x, y);
  }
}

export { SvgStamp as default, STAMP_DEFAULT_SIZE };
